import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { usePOS } from "@/context/POSContext";
import { Switch } from "@/components/ui/switch";
import { listUsers, updateUser } from "@/lib/adminApi";
import { getConnect, setConnect } from "@/lib/connectApi";

const reasonOf = err => {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(d => d.msg).join("; ");
  return err?.message || "unknown error";
};

const ROLE = { admin: "Admin", captain: "Captain", cashier: "Cashier" };
const REFRESH_MS = 10000;
const TONE = { connected: "text-action", connecting: "text-muted-foreground", off: "text-muted-foreground", failed: "text-destructive" };

/**
 * Admin → Integrations → Remote access (RestoYaar Connect, bible §64). The switch
 * (off by default, admin only, audit-logged), the tunnel's state in plain words,
 * and who may sign in from outside. Billing never depends on any of it.
 */
export default function RemoteAccessPanel() {
  const { token, user } = usePOS();
  // The switch and the grants are the owner's door to the internet: admin only.
  const isAdmin = user?.role === "admin";
  const [view, setView] = useState(null);
  const [people, setPeople] = useState([]);
  const [loadError, setLoadError] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [v, users] = await Promise.all([getConnect(token), listUsers(token)]);
      setView(v);
      setPeople((users || []).filter(u => u.is_active));
      setLoadError(null);
    } catch (err) {
      setLoadError(reasonOf(err));
    }
  }, [token]);

  useEffect(() => {
    if (!isAdmin) return undefined;
    load();
    const id = setInterval(load, REFRESH_MS);
    return () => clearInterval(id);
  }, [load, isAdmin]);

  const flip = async on => {
    setBusy(true);
    try {
      setView(await setConnect(on, token));
      toast.success(on ? "Remote access switched on" : "Remote access switched off");
    } catch (err) {
      toast.error(`Not changed — ${reasonOf(err)}`);
    } finally {
      setBusy(false);
    }
  };

  const grant = async (person, on) => {
    setBusy(true);
    try {
      await updateUser(person.id, { remote_access: on }, token);
      toast.success(on ? `${person.name} can sign in from outside` : `${person.name} can no longer sign in from outside`);
      await load();
    } catch (err) {
      toast.error(`Not changed — ${reasonOf(err)}`);
    } finally {
      setBusy(false);
    }
  };

  if (!isAdmin) return null;
  if (loadError) return <p className="text-meta text-destructive" role="alert">Remote access not loaded — {loadError}</p>;
  if (!view) return <p className="text-meta text-muted-foreground">Loading…</p>;

  return (
    <section className="rounded-md border bg-card p-3 space-y-3" data-testid="remote-access-panel">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-body font-medium">Remote access</div>
          <p className="text-meta text-muted-foreground">
            Lets the people you allow open this restaurant's back office from anywhere, through RestoYaar Connect.
            Your data stays on this server and passes through RestoYaar encrypted. Billing never depends on it.
          </p>
        </div>
        <Switch
          checked={view.enabled}
          disabled={busy}
          onCheckedChange={flip}
          aria-label="Remote access"
          data-testid="remote-access-switch"
        />
      </div>
      <p className={`text-meta ${TONE[view.state] || ""}`} data-testid="remote-access-state" role="status">
        {view.words}
      </p>

      {/* Folded while remote access is off and nobody holds it (owner walkthrough,
          2026-10-31: twenty switches for a door that is shut buried Telegram). */}
      {!view.enabled && !people.some(p => p.remote_access) ? (
        <p className="text-meta text-muted-foreground border-t pt-2" data-testid="remote-access-people-folded">
          Switch remote access on to choose who may sign in from outside.
        </p>
      ) : (
      <div className="border-t pt-2 space-y-1">
        <div className="text-body font-medium">Who may sign in from outside</div>
        <p className="text-meta text-muted-foreground">
          They sign in with their password, never the PIN. Nobody is allowed until you switch them on here.
        </p>
        {people.map(p => (
          <div key={p.id} className="flex items-center justify-between gap-2 py-1">
            <span className="text-body min-w-0 truncate">
              {p.name} <span className="text-muted-foreground">· {ROLE[p.role] || p.role} · {p.employee_id}</span>
            </span>
            <Switch
              checked={!!p.remote_access}
              disabled={busy}
              onCheckedChange={on => grant(p, on)}
              aria-label={`Remote access for ${p.name}`}
              data-testid={`remote-access-person-${p.id}`}
            />
          </div>
        ))}
      </div>
      )}
    </section>
  );
}
